import activityData from "@data/activity-data.json";
import type {
  Activity,
  ActivityData,
  Level,
  Role,
  Tool,
} from "@app-types/activity-data.types";

const data = activityData as ActivityData;

const unique = <T>(values: Iterable<T>): T[] => [...new Set(values)];

export const getActivities = (): Activity[] => data.activities;

export const getActivityById = (activityId: string): Activity | undefined =>
  data.activities.find((activity) => activity.id === activityId);

export const getRoles = (): Role[] =>
  unique(
    data.activities.flatMap(
      (activity) => Object.keys(activity.preferences) as Role[],
    ),
  );

export const getToolsForRole = (role: Role): Tool[] =>
  unique(
    data.activities
      .filter((activity) => activity.preferences[role])
      .map((activity) => activity.tool),
  );

export const isToolCompleteForRole = (
  role: Role,
  tool: Tool,
  completedActivityIds: string[],
): boolean => {
  const matchingActivities = data.activities.filter(
    (activity) => activity.tool === tool && activity.preferences[role],
  );

  return (
    matchingActivities.length > 0 &&
    matchingActivities.every((activity) =>
      completedActivityIds.includes(activity.id),
    )
  );
};

export const isLevelCompleteForRoleTool = (
  role: Role,
  tool: Tool,
  level: Level,
  completedActivityIds: string[],
): boolean => {
  const matchingActivities = data.activities.filter(
    (activity) =>
      activity.tool === tool &&
      activity.preferences[role]?.includes(level),
  );

  return (
    matchingActivities.length > 0 &&
    matchingActivities.every((activity) =>
      completedActivityIds.includes(activity.id),
    )
  );
};

const levelOrder: Level[] = ["beginner", "intermediate", "advanced"];
export const getLevelsForRoleTool = (role: Role, tool: Tool): Level[] => {
  const foundLevels = unique(
    data.activities
      .filter(
        (activity) => activity.tool === tool && activity.preferences[role],
      )
      .flatMap((activity) => activity.preferences[role] ?? []),
  );

  return foundLevels.sort(
    (a, b) => levelOrder.indexOf(a) - levelOrder.indexOf(b),
  );
};

export const getActivitiesForSelection = (
  role: Role,
  tool: Tool,
  level: Level,
): Activity[] =>
  data.activities.filter(
    (activity) =>
      activity.tool === tool && activity.preferences[role]?.includes(level),
  );

export const getAvailableActivitiesForSelection = (
  role: Role,
  tool: Tool,
  level: Level,
  completedActivityIds: string[],
): Activity[] =>
  getActivitiesForSelection(role, tool, level).filter(
    (activity) => !completedActivityIds.includes(activity.id),
  );

export const isActivityComplete = (
  activityId: string,
  completedActivityIds: string[],
): boolean => completedActivityIds.includes(activityId);

export const getActivityOptionsForSelection = (
  role: Role,
  tool: Tool,
  level: Level,
  completedActivityIds: string[],
): {
  availableActivities: Activity[];
  recommendedActivities: { activity: Activity; level: Level }[];
} => {
  const availableActivities = getAvailableActivitiesForSelection(
    role,
    tool,
    level,
    completedActivityIds,
  );
  const nextLevel = levelOrder[levelOrder.indexOf(level) + 1];
  const recommendationLevel = availableActivities.length ? level : nextLevel;
  let candidates = availableActivities;
  if (!candidates.length && nextLevel) {
    candidates = getAvailableActivitiesForSelection(
      role,
      tool,
      nextLevel,
      completedActivityIds,
    );
  }

  return {
    availableActivities,
    recommendedActivities: recommendationLevel
      ? candidates.map((activity) => ({ activity, level: recommendationLevel }))
      : [],
  };
};
